import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Trash2, Zap, Download, Loader2, Terminal, Copy, Check } from 'lucide-react';
import {
  useSshHost,
  useCreateSshHost,
  useUpdateSshHost,
  useDeleteSshHost,
  useTestSshHost,
  useInstallClaude,
  useSshHostStats,
  useGenerateSshSetupCommand,
} from '../hooks/useSshHosts';

export default function SshHostForm() {
  const { id } = useParams<{ id: string }>();
  const isEdit = id && id !== 'new';
  const navigate = useNavigate();

  const { data: existingHost } = useSshHost(isEdit ? id : undefined);
  const { data: stats } = useSshHostStats(isEdit ? id : undefined);
  const createMutation = useCreateSshHost();
  const updateMutation = useUpdateSshHost();
  const deleteMutation = useDeleteSshHost();
  const testMutation = useTestSshHost();
  const installMutation = useInstallClaude();
  const setupCommandMutation = useGenerateSshSetupCommand();
  const [setupCopied, setSetupCopied] = useState(false);

  const [name, setName] = useState('');
  const [hostname, setHostname] = useState('');
  const [port, setPort] = useState(22);
  const [sshUser, setSshUser] = useState('');
  const [authMethod, setAuthMethod] = useState<'key' | 'agent' | 'password'>('key');
  const [privateKey, setPrivateKey] = useState('');
  const [passphrase, setPassphrase] = useState('');
  const [claudeBinaryPath, setClaudeBinaryPath] = useState('claude');
  const [workingDirectory, setWorkingDirectory] = useState('');
  const [maxConcurrentInstances, setMaxConcurrentInstances] = useState(5);
  const [connectionPoolSize, setConnectionPoolSize] = useState(3);

  useEffect(() => {
    if (existingHost) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setName(existingHost.name);
      setHostname(existingHost.hostname);
      setPort(existingHost.port);
      setSshUser(existingHost.sshUser);
      setAuthMethod(existingHost.authMethod);
      setClaudeBinaryPath(existingHost.claudeBinaryPath);
      setWorkingDirectory(existingHost.workingDirectory || '');
      setMaxConcurrentInstances(existingHost.maxConcurrentInstances);
      setConnectionPoolSize(existingHost.connectionPoolSize);
    }
  }, [existingHost]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const data = {
      name,
      hostname,
      port,
      sshUser,
      authMethod,
      ...(privateKey ? { privateKey } : {}),
      ...(passphrase ? { passphrase } : {}),
      claudeBinaryPath,
      workingDirectory: workingDirectory || undefined,
      maxConcurrentInstances,
      connectionPoolSize,
    };

    if (isEdit) {
      await updateMutation.mutateAsync({ id, ...data });
    } else {
      await createMutation.mutateAsync(data);
    }
    navigate('/ssh-hosts');
  };

  const handleDelete = async () => {
    if (!isEdit) return;
    if (!confirm('Delete this SSH host? This cannot be undone.')) return;
    await deleteMutation.mutateAsync(id);
    navigate('/ssh-hosts');
  };

  const handleTest = () => {
    if (!isEdit) return;
    testMutation.mutate(id);
  };

  const handleInstall = () => {
    if (!isEdit) return;
    installMutation.mutate(id);
  };

  const handleGenerateSetupCommand = () => {
    if (!isEdit) return;
    setupCommandMutation.mutate(id);
  };

  const handleCopySetupCommand = async () => {
    if (!setupCommandMutation.data?.setupCommand) return;
    await navigator.clipboard.writeText(setupCommandMutation.data.setupCommand);
    setSetupCopied(true);
    setTimeout(() => setSetupCopied(false), 2000);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setPrivateKey(reader.result as string);
    reader.readAsText(file);
  };

  const isPending =
    createMutation.isPending || updateMutation.isPending;

  return (
    <div className="p-8">
      <button
        onClick={() => navigate('/ssh-hosts')}
        className="mb-4 flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to SSH Hosts
      </button>

      <h1 className="mb-6 text-2xl font-bold text-slate-900">
        {isEdit ? 'Edit SSH Host' : 'Add SSH Host'}
      </h1>

      <form onSubmit={handleSubmit} className="max-w-2xl space-y-6">
        {/* Basic Info */}
        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <h2 className="mb-4 text-sm font-semibold text-slate-900">
            Connection Details
          </h2>

          <div className="space-y-4">
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">
                Display Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-400 focus:outline-none"
                placeholder="e.g. Production Server"
                required
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2">
                <label className="mb-1 block text-xs font-medium text-slate-600">
                  Hostname / IP
                </label>
                <input
                  type="text"
                  value={hostname}
                  onChange={(e) => setHostname(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-400 focus:outline-none"
                  placeholder="e.g. 10.200.40.5"
                  required
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">
                  Port
                </label>
                <input
                  type="number"
                  value={port}
                  onChange={(e) => setPort(Number(e.target.value))}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-400 focus:outline-none"
                  min={1}
                  max={65535}
                />
              </div>
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">
                SSH Username
              </label>
              <input
                type="text"
                value={sshUser}
                onChange={(e) => setSshUser(e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-400 focus:outline-none"
                placeholder="e.g. deploy"
                required
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">
                Auth Method
              </label>
              <select
                value={authMethod}
                onChange={(e) =>
                  setAuthMethod(e.target.value as 'key' | 'agent' | 'password')
                }
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-400 focus:outline-none"
              >
                <option value="key">SSH Key</option>
                <option value="agent">SSH Agent</option>
                <option value="password">Password</option>
              </select>
            </div>

            {authMethod === 'key' && (
              <>
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-600">
                    Private Key {isEdit && '(leave empty to keep existing)'}
                  </label>
                  <div className="space-y-2">
                    <input
                      type="file"
                      onChange={handleFileUpload}
                      className="block w-full text-xs text-slate-500 file:mr-4 file:rounded-lg file:border-0 file:bg-slate-100 file:px-4 file:py-2 file:text-xs file:font-medium file:text-slate-700 hover:file:bg-slate-200"
                      accept=".pem,.key,id_*"
                    />
                    <textarea
                      value={privateKey}
                      onChange={(e) => setPrivateKey(e.target.value)}
                      className="w-full rounded-lg border border-slate-200 px-3 py-2 font-mono text-xs focus:border-slate-400 focus:outline-none"
                      rows={4}
                      placeholder="-----BEGIN OPENSSH PRIVATE KEY-----&#10;..."
                    />
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-600">
                    Key Passphrase (optional)
                  </label>
                  <input
                    type="password"
                    value={passphrase}
                    onChange={(e) => setPassphrase(e.target.value)}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-400 focus:outline-none"
                    placeholder="Leave empty if key is not encrypted"
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {/* Claude Configuration */}
        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <h2 className="mb-4 text-sm font-semibold text-slate-900">
            Claude Configuration
          </h2>

          <div className="space-y-4">
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">
                Claude Binary Path
              </label>
              <input
                type="text"
                value={claudeBinaryPath}
                onChange={(e) => setClaudeBinaryPath(e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-400 focus:outline-none"
                placeholder="claude"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">
                Working Directory (optional)
              </label>
              <input
                type="text"
                value={workingDirectory}
                onChange={(e) => setWorkingDirectory(e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-400 focus:outline-none"
                placeholder="/home/deploy/projects"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">
                  Max Concurrent Instances
                </label>
                <input
                  type="number"
                  value={maxConcurrentInstances}
                  onChange={(e) =>
                    setMaxConcurrentInstances(Number(e.target.value))
                  }
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-400 focus:outline-none"
                  min={1}
                  max={20}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">
                  Connection Pool Size
                </label>
                <input
                  type="number"
                  value={connectionPoolSize}
                  onChange={(e) =>
                    setConnectionPoolSize(Number(e.target.value))
                  }
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-400 focus:outline-none"
                  min={1}
                  max={10}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Stats (edit mode only) */}
        {isEdit && stats && (
          <div className="rounded-xl border border-slate-200 bg-white p-6">
            <h2 className="mb-4 text-sm font-semibold text-slate-900">
              Host Statistics
            </h2>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-slate-900">
                  {stats.activeInstances}
                </div>
                <div className="text-xs text-slate-500">Active Instances</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-900">
                  {stats.totalInstances}
                </div>
                <div className="text-xs text-slate-500">Total Instances</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-900">
                  {stats.connectionPoolSize}
                </div>
                <div className="text-xs text-slate-500">Pool Connections</div>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={isPending}
            className="flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-slate-800 disabled:opacity-50"
          >
            {isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {isEdit ? 'Save Changes' : 'Create SSH Host'}
          </button>

          {isEdit && (
            <>
              <button
                type="button"
                onClick={handleTest}
                disabled={testMutation.isPending}
                className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50"
              >
                {testMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Zap className="h-4 w-4" />
                )}
                Test Connection
              </button>

              <button
                type="button"
                onClick={handleInstall}
                disabled={installMutation.isPending}
                className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50"
              >
                {installMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                Install Claude
              </button>

              <button
                type="button"
                onClick={handleGenerateSetupCommand}
                disabled={setupCommandMutation.isPending}
                className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50"
              >
                {setupCommandMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Terminal className="h-4 w-4" />
                )}
                Generate Setup Command
              </button>

              <button
                type="button"
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
                className="ml-auto flex items-center gap-2 rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-medium text-red-600 transition-colors hover:bg-red-50 disabled:opacity-50"
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </button>
            </>
          )}
        </div>

        {/* Test result */}
        {testMutation.data && (
          <div
            className={`rounded-lg border p-4 text-sm ${
              testMutation.data.success
                ? 'border-green-200 bg-green-50 text-green-800'
                : 'border-red-200 bg-red-50 text-red-800'
            }`}
          >
            <p className="font-medium">
              {testMutation.data.success ? 'Connection successful' : 'Connection failed'}
            </p>
            <p className="mt-1 text-xs">{testMutation.data.message}</p>
            {testMutation.data.claudeVersion && (
              <p className="mt-1 text-xs">
                Claude version: {testMutation.data.claudeVersion}
              </p>
            )}
          </div>
        )}

        {/* Install result */}
        {installMutation.data && (
          <div
            className={`rounded-lg border p-4 text-sm ${
              installMutation.data.success
                ? 'border-green-200 bg-green-50 text-green-800'
                : 'border-red-200 bg-red-50 text-red-800'
            }`}
          >
            <p className="font-medium">
              {installMutation.data.success
                ? 'Claude installed successfully'
                : 'Installation failed'}
            </p>
            <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap text-xs">
              {installMutation.data.output}
            </pre>
          </div>
        )}

        {/* Setup command result */}
        {setupCommandMutation.data && (
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm">
            <div className="mb-2 flex items-center justify-between">
              <p className="font-medium text-blue-900">Setup Command (valid 30 min)</p>
              <button
                type="button"
                onClick={handleCopySetupCommand}
                className="flex items-center gap-1.5 rounded-md bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800 hover:bg-blue-200"
              >
                {setupCopied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                {setupCopied ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <p className="mb-2 text-xs text-blue-700">
              Run this command on the remote machine to install Claude Code:
            </p>
            <code className="block break-all rounded bg-white px-3 py-2 font-mono text-xs text-blue-900">
              {setupCommandMutation.data.setupCommand}
            </code>
            <p className="mt-2 text-xs text-blue-600">
              Expires: {new Date(setupCommandMutation.data.expiresAt).toLocaleTimeString()}
            </p>
          </div>
        )}
      </form>
    </div>
  );
}
